import { Controller } from "@hotwired/stimulus"

const DAY = 86400
const PLAY_SECONDS = 30
const BARS = 10
const PALETTE = [ "#e11d48", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#0ea5e9", "#84cc16" ]
const FONT = "ui-rounded, 'SF Pro Rounded', ui-sans-serif, system-ui, sans-serif"
const dateFormat = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" })

// A bar chart race of stamps per event over time, above the running total of passports.
export default class extends Controller {
  static targets = [ "bars", "curve", "date", "playIcon", "pauseIcon" ]
  static values = { journeys: Object }

  connect() {
    const { events, passports } = this.journeysValue
    this.events = events.map((event, i) => ({ name: event.name, times: [], color: PALETTE[i % PALETTE.length], y: null }))
    passports.forEach((passport) => passport.stamps.forEach(([ e, t ]) => this.events[e].times.push(t)))
    this.events.forEach((event) => event.times.sort((a, b) => a - b))
    this.firsts = passports.map((passport) => passport.stamps[0][1]).sort((a, b) => a - b)
    this.start = (this.firsts[0] ?? Date.now() / 1000) - DAY
    this.end = Math.max(...this.events.map((event) => event.times[event.times.length - 1] ?? 0), this.start + DAY) + DAY
    this.time = this.start
    this.playing = !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (!this.playing) this.time = this.end
    this.showPlaying()

    this.resizeObserver = new ResizeObserver(() => this.resize())
    this.resizeObserver.observe(this.barsTarget)
    this.resize()
    this.tick = this.tick.bind(this)
    this.frame = requestAnimationFrame(this.tick)
  }

  disconnect() {
    cancelAnimationFrame(this.frame)
    this.resizeObserver.disconnect()
  }

  toggle() {
    if (!this.playing && this.time >= this.end) this.time = this.start
    this.playing = !this.playing
    this.showPlaying()
  }

  showPlaying() {
    this.playIconTarget.classList.toggle("hidden", this.playing)
    this.pauseIconTarget.classList.toggle("hidden", !this.playing)
  }

  resize() {
    const dpr = window.devicePixelRatio || 1
    for (const canvas of [ this.barsTarget, this.curveTarget ]) {
      canvas.width = Math.round(canvas.clientWidth * dpr)
      canvas.height = Math.round(canvas.clientHeight * dpr)
      canvas.getContext("2d").setTransform(dpr, 0, 0, dpr, 0, 0)
    }
  }

  tick(now) {
    const elapsed = this.lastFrame ? Math.min(0.1, (now - this.lastFrame) / 1000) : 0
    this.lastFrame = now
    if (this.playing) {
      this.time = Math.min(this.end, this.time + (elapsed * (this.end - this.start)) / PLAY_SECONDS)
      if (this.time >= this.end) {
        this.playing = false
        this.showPlaying()
      }
    }
    this.drawBars(elapsed)
    this.drawCurve()
    this.dateTarget.textContent = dateFormat.format(new Date(this.time * 1000))
    this.frame = requestAnimationFrame(this.tick)
  }

  drawBars(elapsed) {
    const canvas = this.barsTarget
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, width, height)

    const ranked = this.events.map((event) => ({ event, value: count(event.times, this.time) }))
      .filter(({ value }) => value > 0)
      .sort((a, b) => b.value - a.value)
    const max = Math.max(1, ranked[0]?.value ?? 1)
    const row = height / BARS
    const k = 1 - Math.exp(-elapsed * 8)
    ctx.font = `600 ${Math.min(14, row * 0.45)}px ${FONT}`
    ctx.textBaseline = "middle"

    ranked.forEach(({ event, value }, rank) => {
      const target = rank < BARS ? rank * row : height + row
      event.y = event.y == null ? target : event.y + (target - event.y) * (elapsed ? k : 1)
      if (event.y > height) return
      const length = Math.max(4, (width - 60) * (value / max))
      const y = event.y + row * 0.12
      const barHeight = row * 0.76
      ctx.fillStyle = event.color
      ctx.beginPath()
      ctx.roundRect(0, y, length, barHeight, 6)
      ctx.fill()
      const label = event.name
      const inside = ctx.measureText(label).width + 16 < length
      ctx.fillStyle = inside ? "#fff" : "#44403c"
      ctx.fillText(label, inside ? 8 : length + 8, y + barHeight / 2)
      ctx.fillStyle = "#78716c"
      const valueX = inside ? length + 8 : length + ctx.measureText(label).width + 16
      ctx.fillText(value.toLocaleString(), valueX, y + barHeight / 2)
    })
    this.events.forEach((event) => { if (!ranked.some((entry) => entry.event === event)) event.y = null })
  }

  drawCurve() {
    const canvas = this.curveTarget
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, width, height)
    const total = Math.max(1, this.firsts.length)
    const x = (t) => ((t - this.start) / (this.end - this.start)) * width
    const y = (n) => height - 18 - (n / total) * (height - 30)

    const outline = (until) => {
      ctx.beginPath()
      ctx.moveTo(0, y(0))
      this.firsts.forEach((t, i) => { if (t <= until) ctx.lineTo(x(t), y(i + 1)) })
      ctx.lineTo(x(until), y(count(this.firsts, until)))
    }
    ctx.lineWidth = 2
    ctx.strokeStyle = "#e7dcc6"
    outline(this.end)
    ctx.stroke()
    ctx.strokeStyle = "#e11d48"
    outline(this.time)
    ctx.stroke()

    const passports = count(this.firsts, this.time)
    ctx.font = `600 13px ${FONT}`
    ctx.fillStyle = "#44403c"
    ctx.textBaseline = "alphabetic"
    const text = `${passports.toLocaleString()} passports`
    ctx.fillText(text, Math.min(width - ctx.measureText(text).width - 4, x(this.time) + 6), Math.max(14, y(passports) - 6))
  }
}

// How many sorted values are at or before t.
function count(values, t) {
  let low = 0
  let high = values.length
  while (low < high) {
    const mid = (low + high) >> 1
    if (values[mid] <= t) low = mid + 1
    else high = mid
  }
  return low
}
